import api from "./axios";
import {
  API_ENDPOINTS,
  DEFAULT_BASE_SUM_ASSURED,
  DEFAULT_POLICY_TERM,
  DEFAULT_PREMIUM_PAYING_TERM,
  PREMIUM_PAYMENT_MODES,
  DEFAULT_LIMITED_PAY_OPTIONS,
  PAYMENT_TERM_TYPE,
  PLAN_NAMES_NISCHIT_PENSION,
} from "../constants";
import { logError, logDebug } from "../common/utils/logger";
import { getCurrentJourneyContext } from "../common/utils/journeyContextRuntime";
import { encryptJourneyQueryValue } from "../common/utils/journeyEncryption";
import { resolveNischitPptAndDp } from "../insurance-journey/quote/productConfig";
const context = getCurrentJourneyContext();
// Re-export constants for backward compatibility
export {
  PREMIUM_PAYMENT_MODES,
  DEFAULT_LIMITED_PAY_OPTIONS,
  PAYMENT_TERM_TYPE,
};

/** Set VITE_DEBUG_PREMIUM=true in .env to trace multi-premium calls (verbose). */
const DEBUG_PREMIUM_MULTIPLEX =
  typeof import.meta !== "undefined" &&
  import.meta.env?.VITE_DEBUG_PREMIUM === "true";

const MULTI_DIMENSION_PREMIUM_CACHE_TTL_MS = 2 * 60 * 1000;
const multiDimensionPremiumResultCache = new Map();
const multiDimensionPremiumInFlightCache = new Map();
/** Dedupe concurrent identical Nishchit bulk payloads (quote UI + journey hooks often fire together). */
const nishchitBulkPremiumInFlightCache = new Map();

const normalizeYesNo = (value) => {
  if (value === undefined || value === null) return undefined;
  const raw = String(value).trim().toLowerCase();
  if (raw === "yes" || raw === "true" || raw === "1") return "Yes";
  if (raw === "no" || raw === "false" || raw === "0") return "No";
  return value;
};

const getAgeFromDob = (dob) => {
  if (!dob) return undefined;
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return undefined;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDelta = today.getMonth() - date.getMonth();
  if (
    monthDelta < 0 ||
    (monthDelta === 0 && today.getDate() < date.getDate())
  ) {
    age -= 1;
  }
  if (age <= 0) return undefined;
  return age;
};

const toTitleFrequency = (value, fallback = undefined) => {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!raw) return fallback;
  if (raw.includes("month")) return "Monthly";
  if (raw.includes("quarter")) return "Quarterly";
  if (raw.includes("half")) return "Half-yearly";
  if (raw.includes("year")) return "Yearly";
  return fallback;
};

const normalizeGender = (value, fallback = undefined) => {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!raw) return fallback;

  // Direct tokens
  if (raw === "m" || raw === "male") return "Male";
  if (raw === "f" || raw === "female") return "Female";
  if (raw === "o" || raw === "other") return "Other";

  // DDM coded values coming from form: e.g. "Option05994254" for Male
  if (raw.includes("option05994254")) return "Male";
  if (raw.includes("option09046092")) return "Female";
  if (raw.includes("option92959134")) return "Other";

  // Relationship/gender can sometimes arrive as "self" resolved labels
  if (raw.includes("option") && raw.includes("male")) return "Male";
  if (raw.includes("option") && raw.includes("female")) return "Female";

  return fallback;
};

const normalizeRelation = (value, fallback = undefined) => {
  const raw = String(value ?? "").trim();
  return raw || fallback;
};

const toStringOrEmpty = (value) =>
  value === undefined || value === null ? "" : String(value);

const getApplicationNumberFromUrl = () => {
  if (typeof window === "undefined" || !window?.location?.search) return "";
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("applicationNumber") || params.get("applicationNo") || "";
  } catch (_err) {
    return "";
  }
};

/**
 * Resolve application number for premium APIs (lead, quote-init, Redux, session, URL).
 */
export const resolveApplicationNumberForPremium = (
  leadResult,
  overrides = {},
  journeyIdentifiers = {},
) => {
  const candidates = [
    overrides?.applicationNumber,
    leadResult?.applicationNumber,
    leadResult?.applicationNo,
    leadResult?.application_number,
    leadResult?.leadInput?.applicationNumber,
    leadResult?.leadInput?.application_number,
    leadResult?.quoteInitResult?.applicationNumber,
    journeyIdentifiers?.applicationNumber,
    journeyIdentifiers?.storedApplicationNumber,
    getApplicationNumberFromUrl(),
  ];
  for (const value of candidates) {
    if (value == null) continue;
    const trimmed = String(value).trim();
    if (trimmed !== "") return trimmed;
  }
  return undefined;
};

const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const deriveNriFromLead = (leadInput = {}, additionalInput = {}) => {
  if (additionalInput?.nri !== undefined && additionalInput?.nri !== null) {
    return String(additionalInput.nri);
  }
  const nationality = String(leadInput?.nationality ?? "")
    .trim()
    .toLowerCase();
  if (!nationality) return "";
  if (nationality === "false" || nationality === "nri") return "Yes";
  if (nationality === "true" || nationality === "indian") return "No";
  return "";
};

const isTruthyOption = (value) => {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  return (
    raw === "true" || raw === "yes" || raw === "1" || raw === "option19854214"
  );
};

const isSinglePayByProductCode = (value) =>
  String(value ?? "").trim() === "22074";

const resolveSinglePayFlag = (
  leadResult = {},
  leadInput = {},
  overrides = {},
  additionalInput = {},
) => {
  // Highest priority: explicit overrides in current request.
  if (additionalInput?.isSinglePay != null) {
    return isTruthyOption(additionalInput.isSinglePay);
  }
  if (overrides?.isSinglePay != null) {
    return isTruthyOption(overrides.isSinglePay);
  }
  // Product code override should force product variant when provided.
  if (overrides?.productCode != null) {
    return isSinglePayByProductCode(overrides.productCode);
  }
  // Lead payload can contain isSinglePay either in leadInput or top-level.
  if (leadInput?.isSinglePay != null) {
    return isTruthyOption(leadInput.isSinglePay);
  }
  if (leadResult?.leadInput?.isSinglePay != null) {
    return isTruthyOption(leadResult.leadInput.isSinglePay);
  }
  if (leadResult?.isSinglePay != null) {
    return isTruthyOption(leadResult.isSinglePay);
  }
  // Last fallback: infer from assigned product code if available.
  if (leadResult?.assignedProductCode != null) {
    return isSinglePayByProductCode(leadResult.assignedProductCode);
  }
  return false;
};

const isJointPlanOption = (planOption) => {
  const option = String(planOption ?? "")
    .trim()
    .toLowerCase();
  return ["option_4", "option_5", "option_6"].includes(option);
};

const resolveMinimumPremiumByPlanOption = (planOption) => {
  const option = String(planOption ?? "")
    .trim()
    .toLowerCase();
  const table = {
    option_1: 100000,
    option_2: 100000,
    option_3: 100000,
    option_4: 100000,
    option_5: 100000,
    option_6: 100000,
  };
  return table[option] ?? null;
};

const stableSerialize = (value) => {
  if (value === null || value === undefined) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return "[" + value.map((item) => stableSerialize(item)).join(",") + "]";
  }

  if (typeof value === "object") {
    const serialized = Object.keys(value)
      .sort()
      .map((key) => {
        return JSON.stringify(key) + ":" + stableSerialize(value[key]);
      })
      .join(",");

    return "{" + serialized + "}";
  }

  return JSON.stringify(value);
};

const cloneSerializable = (value) => {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

const getCachedMultiDimensionPremiumResult = (cacheKey) => {
  const cachedEntry = multiDimensionPremiumResultCache.get(cacheKey);
  if (!cachedEntry) return null;
  if (cachedEntry.expiresAt <= Date.now()) {
    multiDimensionPremiumResultCache.delete(cacheKey);
    return null;
  }
  return cloneSerializable(cachedEntry.result);
};

const setCachedMultiDimensionPremiumResult = (cacheKey, result) => {
  multiDimensionPremiumResultCache.set(cacheKey, {
    result,
    expiresAt: Date.now() + MULTI_DIMENSION_PREMIUM_CACHE_TTL_MS,
  });
};

const resolveState = (leadInput = {}) =>
  leadInput.branchLocationState ||
  leadInput.communicationAddressState ||
  leadInput.state ||
  undefined;

const buildPremiumDefaults = (premiumDefaults = {}) => ({
  premiumFrequency: premiumDefaults.premiumPaymentMode || "Yearly",
  premiumPaymentMode: premiumDefaults.premiumPaymentMode || "Yearly",
  ecs: premiumDefaults.ecs || "Yes",
  channel: premiumDefaults.channel || "Online",
  enhancedCoverageBenefit: premiumDefaults.enhancedCoverageBenefit || "No",
  accidentalDeathBenefitRider:
    premiumDefaults.accidentalDeathBenefitRider || "No",
  accidentalDeathAndDisabilityRider:
    premiumDefaults.accidentalDeathAndDisabilityRider || "No",
  accidentalDeathAndDisabilityPlusRider:
    premiumDefaults.accidentalDeathAndDisabilityPlusRider || "No",
  criticalIllnessRider: premiumDefaults.criticalIllnessRider || "No",
});

/** Frontend rider IDs used in Quote UI */
const RIDER_IDS = {
  ADB: "accidental-death",
  ADD: "accidental-death-disability",
  ADD_PLUS: "accidental-death-disability-plus",
  CI: "critical-illness",
};

const RIDER_LEGACY_FLAG_KEYS = {
  [RIDER_IDS.ADB]: "accidentalDeathBenefitRider",
  [RIDER_IDS.ADD]: "accidentalDeathAndDisabilityRider",
  [RIDER_IDS.ADD_PLUS]: "accidentalDeathAndDisabilityPlusRider",
  [RIDER_IDS.CI]: "criticalIllnessRider",
};

const RIDER_OVERRIDE_CONFIG = [
  { id: RIDER_IDS.ADB, prefix: "riderADB" },
  { id: RIDER_IDS.ADD, prefix: "riderADD" },
  { id: RIDER_IDS.ADD_PLUS, prefix: "riderADDPlus" },
  { id: RIDER_IDS.CI, prefix: "riderCI" },
];

const buildSingleRiderOverrides = (
  riderId,
  prefix,
  has,
  cover,
  riderPT,
  riderPPT,
) => {
  const enabled = has(riderId);
  const yesNo = enabled ? "Yes" : "No";
  return {
    [RIDER_LEGACY_FLAG_KEYS[riderId]]: yesNo,
    [`${prefix}Enabled`]: yesNo,
    [`${prefix}SA`]: enabled ? cover(riderId) : undefined,
    [`${prefix}PolicyTerm`]: enabled && riderPT != null ? riderPT : undefined,
    [`${prefix}PaymentTerm`]:
      enabled && riderPPT != null ? riderPPT : undefined,
  };
};

/**
 * Builds overrides for ROP and ECBO (Scenarios 1–3). Default NO.
 * @param {Object} quoteOptions - { premiumRefundEnabled: boolean, enhancedCoverageEnabled: boolean }
 * @param {boolean} isEliteEligible - from leadResult.assignedProductName
 * @param {string} selectedPlan - active plan display name
 * @returns {{ returnOfPremium?: number, enhancedCoverageBenefit: string }}
 */
export const buildRopEcboOverrides = (
  quoteOptions = {},
  isEliteEligible = false,
  selectedPlan = null,
) => {
  // ROP cell exists for Plus workbook only; keep Elite without ROP payload.
  const rop =
    selectedPlan === PLAN_NAMES_NISCHIT_PENSION.PLAN_A
      ? quoteOptions.premiumRefundEnabled === true
        ? "Single Life Annuity"
        : "Joint Life Annuity"
      : undefined;
  const ecbo = isEliteEligible
    ? quoteOptions.enhancedCoverageEnabled === true
      ? "Yes"
      : "No"
    : "Doesn't meet eligibility";
  return { returnOfPremium: rop, enhancedCoverageBenefit: ecbo };
};

/**
 * Builds premium request overrides for rider selection, cover amounts (SA), and rider Policy Term / PPT.
 * Matches calculation sheet: riderADBEnabled, riderADBSA, riderADBPolicyTerm, riderADBPaymentTerm, etc.
 * Output rider premiums (riderADBPremium, riderADDPremium, riderADDPlusPremium, riderCIPremium) come from API.
 *
 * @param {Set<string>|string[]} selectedRiderIds - Selected rider IDs (e.g. from Quote)
 * @param {Object.<string, number>} riderCovers - Rider ID to cover amount (SA)
 * @param {number} [policyTerm] - Base policy term (years); used for rider PT when provided
 * @param {string} [paymentTermType] - 'Limited Pay' or 'Regular Pay'
 * @param {number} [selectedLimitedPayTerm] - Selected LP years for the current plan when paymentTermType is Limited Pay
 * @returns {Object} Overrides to merge into premium request
 */
export const buildRiderOverridesFromSelection = (
  selectedRiderIds = [],
  riderCovers = {},
  policyTerm = undefined,
  paymentTermType = undefined,
  selectedLimitedPayTerm = undefined,
) => {
  const set =
    selectedRiderIds instanceof Set
      ? selectedRiderIds
      : new Set(selectedRiderIds || []);
  const has = (id) => set.has(id);
  const cover = (id) =>
    riderCovers[id] != null ? Number(riderCovers[id]) : undefined;
  const isLP = paymentTermType === "Limited Pay";
  const riderPPT =
    policyTerm != null
      ? isLP
        ? (selectedLimitedPayTerm ?? 5)
        : policyTerm
      : undefined;
  const riderPT = policyTerm;

  return RIDER_OVERRIDE_CONFIG.reduce(
    (acc, { id, prefix }) => ({
      ...acc,
      ...buildSingleRiderOverrides(id, prefix, has, cover, riderPT, riderPPT),
    }),
    {},
  );
};

const firstNonBlank = (...values) =>
  values.find(
    (v) =>
      v !== undefined &&
      v !== null &&
      !(typeof v === "string" && v.trim() === ""),
  );

/**
 * Resolve lead input from leadResult so premium request has lifeAssuredDob, lifeAssuredGender, etc.
 * When quoteInitResult is used (e.g. after product switch), it may not have leadInput; use top-level fields as fallback to avoid #VALUE! in Excel.
 * Unique persist keys (proposerDob, primaryAnnuitantDob) are first-class; DDM aliases are fallbacks.
 */
const getEffectiveLeadInput = (leadResult) => {
  const leadInput = leadResult?.leadInput || {};
  const r = leadResult || {};
  const nestedPrimary =
    leadInput?.annuitant?.primaryAnnuity ||
    leadInput?.annuitant?.primaryAnnuitant ||
    r?.annuitant?.primaryAnnuity ||
    r?.annuitant?.primaryAnnuitant ||
    {};
  const pick = (...keys) => {
    for (const key of keys) {
      const value = firstNonBlank(leadInput[key], r[key], nestedPrimary[key]);
      if (value != null) return value;
    }
    return undefined;
  };
  return {
    lifeAssuredName: pick("lifeAssuredName", "proposerName", "fullName", "name"),
    lifeAssuredDob: pick(
      "lifeAssuredDob",
      "proposerDob",
      "dateOfBirth",
      "dob",
    ),
    lifeAssuredAge: pick("lifeAssuredAge"),
    lifeAssuredGender: pick(
      "lifeAssuredGender",
      "proposerGender",
      "gender",
    ),
    proposerDob: pick("proposerDob", "dateOfBirth", "dob", "lifeAssuredDob"),
    proposerGender: pick("proposerGender", "gender", "lifeAssuredGender"),
    primaryAnnuitantDob: pick(
      "primaryAnnuitantDob",
      "annuitantDateOfBirth",
    ),
    primaryAnnuitantGender: pick(
      "primaryAnnuitantGender",
      "annuitantGender",
    ),
    annuitantDateOfBirth: pick(
      "annuitantDateOfBirth",
      "primaryAnnuitantDob",
    ),
    annuitantGender: pick("annuitantGender", "primaryAnnuitantGender"),
    secondaryAnnuitantDob: pick("secondaryAnnuitantDob"),
    secondaryAnnuitantGender: pick("secondaryAnnuitantGender"),
    secondaryAnnuitantRelation: pick("secondaryAnnuitantRelation"),
    isSinglePay: pick("isSinglePay"),
    employeeFlag: pick("employeeFlag"),
    salaried: pick("salaried"),
    smokerStatus: pick("smokerStatus"),
    branchLocationState: pick("branchLocationState"),
    communicationAddressState: pick("communicationAddressState"),
    state: pick("state"),
    nationality: pick("nationality", "basedInIndia"),
  };
};

const buildNischitPensionAdditionalInputFields = (
  leadInput,
  additionalInput,
  overrides,
  singlePay,
) => {
  const proposerDob = firstNonBlank(
    additionalInput.proposerDob,
    leadInput.proposerDob,
    leadInput.lifeAssuredDob,
    leadInput.dateOfBirth,
  );
  const proposerGender = normalizeGender(
    firstNonBlank(
      additionalInput.proposerGender,
      leadInput.proposerGender,
      leadInput.lifeAssuredGender,
      leadInput.gender,
    ),
  );
  const primaryAnnuitantDob = firstNonBlank(
    additionalInput.primaryAnnuitantDob,
    leadInput.primaryAnnuitantDob,
    leadInput.annuitantDateOfBirth,
    proposerDob,
  );
  const primaryAnnuitantGender = normalizeGender(
    firstNonBlank(
      additionalInput.primaryAnnuitantGender,
      leadInput.primaryAnnuitantGender,
      leadInput.annuitantGender,
      proposerGender,
      leadInput.lifeAssuredGender,
    ),
  );
  const secondaryAnnuitantDob = firstNonBlank(
    additionalInput.secondaryAnnuitantDob,
    leadInput.secondaryAnnuitantDob,
  );
  const secondaryAnnuitantGender = normalizeGender(
    firstNonBlank(
      additionalInput.secondaryAnnuitantGender,
      leadInput.secondaryAnnuitantGender,
    ),
  );
  const secondaryAnnuitantRelation = firstNonBlank(
    additionalInput.secondaryAnnuitantRelation,
    leadInput.secondaryAnnuitantRelation,
  );
  const planOption = toStringOrEmpty(additionalInput.planOption);
  const jointOptionSelected = isJointPlanOption(planOption);
  const { ppt: resolvedPpt, dp: resolvedDp } = resolveNischitPptAndDp(
    singlePay ? "22074" : "22070",
    additionalInput,
    { premiumPayingTerm: overrides.premiumPayingTerm },
  );
  const minimumPremium = resolveMinimumPremiumByPlanOption(planOption);
  const resolvedPremium =
    toNumberOrNull(
      additionalInput.premium ??
        additionalInput.premiumAmount ??
        overrides.basePremium,
    ) ?? minimumPremium;
  const annuityFrequency =
    toTitleFrequency(additionalInput.annuityFrequency) || "Yearly";

  return {
    proposerDob: toStringOrEmpty(proposerDob),
    proposerGender: toStringOrEmpty(proposerGender),
    primaryAnnuitantDob: toStringOrEmpty(primaryAnnuitantDob),
    primaryAnnuitantGender: toStringOrEmpty(primaryAnnuitantGender),
    secondaryAnnuitantDob: jointOptionSelected
      ? toStringOrEmpty(secondaryAnnuitantDob)
      : "",
    secondaryAnnuitantGender: jointOptionSelected
      ? toStringOrEmpty(secondaryAnnuitantGender)
      : "",
    secondaryAnnuitantRelation: jointOptionSelected
      ? toStringOrEmpty(normalizeRelation(secondaryAnnuitantRelation))
      : "",
    planOption,
    ppt: resolvedPpt,
    dp: resolvedDp,
    annuityFrequency: toStringOrEmpty(annuityFrequency),
    premium: resolvedPremium,
    nri: deriveNriFromLead(leadInput, additionalInput),
  };
};

const buildNischitPensionPremiumRequest = (
  leadResult,
  leadInput,
  overrides,
  premiumDefaults,
) => {
  const additionalInput = overrides?.additionalInputFields || {};
  const singlePay = resolveSinglePayFlag(
    leadResult,
    leadInput,
    overrides,
    additionalInput,
  );
  const premiumFrequency =
    toTitleFrequency(
      overrides.premiumFrequency ?? additionalInput.premiumFrequency,
    ) || "Yearly";

  return {
    productCode: singlePay ? "22074" : "22070",
    premiumFrequency,
    premiumPaymentMode: singlePay ? "Single Pay" : "Yearly",
    channel: toStringOrEmpty(
      overrides.channel ?? premiumDefaults.channel ?? "Online",
    ),
    ecs: toStringOrEmpty(overrides.ecs ?? premiumDefaults.ecs ?? "Yes"),
    additionalInputFields: buildNischitPensionAdditionalInputFields(
      leadInput,
      additionalInput,
      overrides,
      singlePay,
    ),
  };
};

const buildDefaultPremiumRequest = (
  leadResult,
  leadInput,
  overrides,
  defaults,
  derivedAge,
  premiumPayingOption,
) => ({
  applicationNumber: resolveApplicationNumberForPremium(leadResult, overrides),
  productCode: overrides.productCode ?? leadResult.assignedProductCode,
  lifeAssuredName: leadInput.lifeAssuredName,
  lifeAssuredDob: leadInput.lifeAssuredDob,
  lifeAssuredAge: derivedAge ?? leadInput.lifeAssuredAge,
  lifeAssuredGender: leadInput.lifeAssuredGender,
  baseSumAssured: overrides.baseSumAssured ?? DEFAULT_BASE_SUM_ASSURED,
  policyTerm: overrides.policyTerm ?? DEFAULT_POLICY_TERM,
  premiumPayingOption,
  premiumPayingTerm: overrides.premiumPayingTerm ?? DEFAULT_PREMIUM_PAYING_TERM,
  ...defaults,
  premiumFrequency: overrides.premiumFrequency ?? defaults.premiumFrequency,
  premiumPaymentMode: overrides.premiumFrequency ?? defaults.premiumPaymentMode,
  employeeLifeAssured: normalizeYesNo(leadInput.employeeFlag),
  branchLocationState: resolveState(leadInput),
  communicationAddressState: resolveState(leadInput),
  salaried: normalizeYesNo(leadInput.salaried),
  smokerStatus: normalizeYesNo(leadInput.smokerStatus),
  riderADBEnabled: normalizeYesNo(overrides.riderADBEnabled),
  riderADBSA: overrides.riderADBSA ?? undefined,
  riderADDEnabled: normalizeYesNo(overrides.riderADDEnabled),
  riderADDSA: overrides.riderADDSA ?? undefined,
  riderADDPlusEnabled: normalizeYesNo(overrides.riderADDPlusEnabled),
  riderADDPlusSA: overrides.riderADDPlusSA ?? undefined,
  riderCIEnabled: normalizeYesNo(overrides.riderCIEnabled),
  riderCISA: overrides.riderCISA ?? undefined,
  riderADBPolicyTerm: overrides.riderADBPolicyTerm ?? undefined,
  riderADBPaymentTerm: overrides.riderADBPaymentTerm ?? undefined,
  riderADDPolicyTerm: overrides.riderADDPolicyTerm ?? undefined,
  riderADDPaymentTerm: overrides.riderADDPaymentTerm ?? undefined,
  riderADDPlusPolicyTerm: overrides.riderADDPlusPolicyTerm ?? undefined,
  riderADDPlusPaymentTerm: overrides.riderADDPlusPaymentTerm ?? undefined,
  riderCIPolicyTerm: overrides.riderCIPolicyTerm ?? undefined,
  riderCIPaymentTerm: overrides.riderCIPaymentTerm ?? undefined,
  returnOfPremium: overrides.returnOfPremium ?? undefined,
  enhancedCoverageBenefit:
    overrides.enhancedCoverageBenefit ?? defaults.enhancedCoverageBenefit,
  riderPolicyTerm: overrides.riderPolicyTerm ?? undefined,
  riderPaymentTerm: overrides.riderPaymentTerm ?? undefined,
  riderCIPT: overrides.riderCIPT ?? undefined,
  riderCIPPT: overrides.riderCIPPT ?? undefined,
  premiumPaymentModeCode: overrides.premiumPaymentModeCode ?? undefined,
});

export const buildPremiumRequestFromLead = (leadResult, overrides = {}) => {
  if (!leadResult) return null;

  const leadInput = getEffectiveLeadInput(leadResult);
  const premiumDefaults = leadResult.premiumDefaults || {};
  const derivedAge = getAgeFromDob(leadInput.lifeAssuredDob);
  const defaults = buildPremiumDefaults(premiumDefaults);
  const premiumPayingOption =
    overrides.premiumPayingOption ??
    (overrides.paymentTermType === "Limited Pay" ? "Limited" : "Regular");

  if (context?.journeyType === "nischit-pension") {
    return buildNischitPensionPremiumRequest(
      leadResult,
      leadInput,
      overrides,
      premiumDefaults,
    );
  }

  return buildDefaultPremiumRequest(
    leadResult,
    leadInput,
    overrides,
    defaults,
    derivedAge,
    premiumPayingOption,
  );
};

const buildMultiDimensionPremiumRequestPayload = (
  leadResult,
  overrides = {},
  options = {},
) => {
  const baseRequest = buildPremiumRequestFromLead(leadResult, overrides);
  if (!baseRequest?.productCode) {
    throw new Error(
      "Product code is missing for multi-dimension premium calculation.",
    );
  }

  const {
    primaryMode = leadResult?.premiumDefaults?.premiumPaymentMode || "Yearly",
    primaryTermType = PAYMENT_TERM_TYPE.REGULAR_PAY,
    primaryPremiumPayingTerm = null,
    limitedPayOptions = DEFAULT_LIMITED_PAY_OPTIONS,
    regularPayTerm = baseRequest.policyTerm || DEFAULT_POLICY_TERM,
    calculateAllPaymentTermTypes = true,
    returnAllResults = true,
  } = options;

  return {
    primaryMode,
    primaryTermType,
    requestPayload: {
      baseRequest,
      primaryPremiumPaymentMode: primaryMode,
      premiumPaymentModes: PREMIUM_PAYMENT_MODES,
      primaryPaymentTermType: primaryTermType,
      primaryPremiumPayingTerm:
        primaryPremiumPayingTerm ||
        (primaryTermType === PAYMENT_TERM_TYPE.LIMITED_PAY
          ? limitedPayOptions[0]
          : regularPayTerm),
      limitedPayTermOptions: limitedPayOptions,
      regularPayTerm,
      calculateAllPaymentTermTypes,
      returnAllResults,
    },
  };
};

const parsePremiumJsonMaybe = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const calculatePremiumFromLead = async (leadResult, overrides = {}) => {
  try {
    const payload = buildPremiumRequestFromLead(leadResult, overrides);
    if (!payload?.productCode) {
      throw new Error("Product code is missing for premium calculation.");
    }

    if (
      context?.journeyType === "nischit-pension" &&
      import.meta.env?.VITE_DEBUG_NISCHIT_CALCULATE === "true"
    ) {
      logDebug("[Nischit calculate payload]", payload);
    }

    const response = await api.post(API_ENDPOINTS.CALCULATE_PREMIUM, payload);
    const data = response?.data ?? null;    

    let parsed = parsePremiumJsonMaybe(data);

    if (parsed && typeof parsed === "object") {
      return {
        productName: leadResult?.assignedProductName,
        ...parsed,
      };
    }

    return {
      calculationSuccessful: true,
      productName: leadResult?.assignedProductName,
    };
  } catch (error) {
    logError("[calculatePremiumFromLead] Failed to calculate premium", error);
    throw error;
  }
};



const parsePremiumResponseData = (data) => {
  let parsed = parsePremiumJsonMaybe(data);
  if (typeof parsed === "string" && parsed.trim().startsWith("{")) {
    parsed = parsePremiumJsonMaybe(parsed);
  }
  return parsed;
};

const encryptPremiumRequestFields = (request) => {
  const encrypted = { ...request };
  if (
    encrypted.applicationNumber != null &&
    String(encrypted.applicationNumber).trim() !== ""
  ) {
    encrypted.applicationNumber = encryptJourneyQueryValue(
      encrypted.applicationNumber,
    );
  }
  if (
    encrypted.lifeAssuredDob != null &&
    String(encrypted.lifeAssuredDob).trim() !== ""
  ) {
    encrypted.lifeAssuredDob = encryptJourneyQueryValue(
      encrypted.lifeAssuredDob,
    );
  }
  return encrypted;
};

const buildRelevantPremiumError = (payload) => {
  const primary = payload?.primaryResult || {};
  const relevantCode =
    payload?.errorCode ||
    primary?.errorCode ||
    payload?.excelErrorCode ||
    primary?.excelErrorCode ||
    null;

  if (relevantCode) {
    return `Premium calculation failed: ${relevantCode}`;
  }

  const relevantMessage =
    payload?.errorMessage || primary?.errorMessage || null;
  if (typeof relevantMessage === "string" && relevantMessage.trim()) {
    return relevantMessage.trim();
  }

  return "Premium calculation could not be completed for the selected options.";
};

const rowHasPremiumFigure = (row) => {
  if (!row || typeof row !== "object") return false;
  const hasFigure = (v) => v != null && v !== "" && !Number.isNaN(Number(v));
  return (
    hasFigure(row.finalDiscountedPremium) ||
    hasFigure(row.totalPayableAmount) ||
    hasFigure(row.premiumAmount) ||
    hasFigure(row.finalPremium) ||
    hasFigure(row.annualPremium)
  );
};

const mapHasPremiumFigure = (map) => {
  if (!map || typeof map !== "object") return false;
  return Object.keys(map).some((key) => rowHasPremiumFigure(map[key]));
};

const multiPayloadHasPremiumFigures = (payload) => {
  if (!payload || typeof payload !== "object") return false;
  return (
    rowHasPremiumFigure(payload.primaryResult) ||
    rowHasPremiumFigure(payload.regularPayResult) ||
    mapHasPremiumFigure(payload.allResults) ||
    mapHasPremiumFigure(payload.combinedResults) ||
    mapHasPremiumFigure(payload.limitedPayResults)
  );
};

const assignProductNameToResultMap = (resultMap, productName) => {
  if (!resultMap) return;
  Object.keys(resultMap).forEach((key) => {
    if (resultMap[key]) {
      resultMap[key].productName = productName;
    }
  });
};

const attachProductNameToMultiDimensionResult = (parsed, productName) => {
  if (parsed.primaryResult) {
    parsed.primaryResult.productName = productName;
  }
  assignProductNameToResultMap(parsed.allResults, productName);
  assignProductNameToResultMap(parsed.limitedPayResults, productName);
  if (parsed.regularPayResult) {
    parsed.regularPayResult.productName = productName;
  }
  assignProductNameToResultMap(parsed.combinedResults, productName);
};

const attachProductNameToBulkMultiResult = (multiResult, productName) => {
  if (!multiResult) return;
  if (multiResult.primaryResult)
    multiResult.primaryResult.productName = productName;
  if (multiResult.allResults) {
    Object.values(multiResult.allResults).forEach((r) => {
      if (r) r.productName = productName;
    });
  }
  if (multiResult.limitedPayResults) {
    Object.values(multiResult.limitedPayResults).forEach((r) => {
      if (r) r.productName = productName;
    });
  }
  if (multiResult.regularPayResult)
    multiResult.regularPayResult.productName = productName;
  if (multiResult.combinedResults) {
    Object.values(multiResult.combinedResults).forEach((r) => {
      if (r) r.productName = productName;
    });
  }
};

const processMultiDimensionPremiumResponse = (data, leadResult, cacheKey) => {
  const parsed = parsePremiumResponseData(data);
  if (!parsed || typeof parsed !== "object") {
    throw new Error(
      "Premium service returned an empty or invalid response body.",
    );
  }

  if (
    parsed.calculationSuccessful === false ||
    !multiPayloadHasPremiumFigures(parsed)
  ) {
    throw new Error(buildRelevantPremiumError(parsed));
  }

  attachProductNameToMultiDimensionResult(
    parsed,
    leadResult?.assignedProductName,
  );

  if (DEBUG_PREMIUM_MULTIPLEX) {
    logDebug("[calculateMultiDimensionPremium] API response:", {
      calculationSuccessful: parsed.calculationSuccessful,
      calculationTimeMs: parsed.calculationTimeMs,
      primaryMode: parsed.primaryPremiumPaymentMode,
      primaryTermType: parsed.primaryPaymentTermType,
      modeResultCount: parsed.allResults
        ? Object.keys(parsed.allResults).length
        : 0,
      lpResultCount: parsed.limitedPayResults
        ? Object.keys(parsed.limitedPayResults).length
        : 0,
      combinedResultCount: parsed.combinedResults
        ? Object.keys(parsed.combinedResults).length
        : 0,
    });
  }

  setCachedMultiDimensionPremiumResult(cacheKey, parsed);
  return parsed;
};

const resolveMultiDimensionPremiumErrorMessage = (error) => {
  const responseData = error?.response?.data;
  return (
    (typeof responseData === "object" && responseData !== null
      ? responseData.errorMessage || responseData.message
      : null) ||
    error?.message ||
    "Premium calculation could not be completed for the selected options."
  );
};

const normalizeNishchitBulkParsedRoot = (data) =>
  parsePremiumResponseData(data);

const OPTION_RESULT_KEY_RE = /^option_\d+$/i;

/** Some gateways stringify `additionalOutputFields` or wrap decimals in `{ value: "..." }`. */
const normalizeAdditionalOutputFields = (raw) => {
  if (raw == null) return {};
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};4
    const parsed = parsePremiumJsonMaybe(trimmed);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  }
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  return {};
};

const coerceAnnuityScalar = (v) => {
  if (v == null || v === "") return undefined;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "object" && v !== null && "value" in v) {
    return coerceAnnuityScalar(v.value);
  }
  if (typeof v === "string") {
    const cleaned = v.replace(/,/g, "").trim();
    if (!cleaned) return undefined;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

const isOptionResultsMap = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  const optionKeys = Object.keys(obj).filter((k) =>
    OPTION_RESULT_KEY_RE.test(String(k)),
  );
  if (optionKeys.length === 0) return false;
  return optionKeys.some((k) => {
    const row = obj[k];
    if (!row || typeof row !== "object" || Array.isArray(row)) return false;
    return (
      row.additionalOutputFields != null ||
      row.calculationSuccessful !== undefined ||
      row.annuityPerFrequency != null ||
      row.annuityRate != null
    );
  });
};

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const unwrapNishchitResultsValue = (resultsValue) => {
  if (resultsValue == null) return null;
  if (typeof resultsValue === "string") {
    const inner = normalizeNishchitBulkParsedRoot(resultsValue);
    return isPlainObject(inner) ? inner : null;
  }
  return isPlainObject(resultsValue) ? resultsValue : null;
};

const walkNishchitArrayChildren = (node, depth, seen) => {
  for (const child of node) {
    const hit = walkFindNishchitOptionResultsMap(child, depth + 1, seen);
    if (hit) return hit;
  }
  return null;
};

const walkNishchitObjectChildren = (node, depth, seen) => {
  const vals = Object.values(node);
  for (let i = 0; i < vals.length; i += 1) {
    const v = vals[i];
    if (v == null || (typeof v !== "object" && typeof v !== "string")) continue;
    const hit = walkFindNishchitOptionResultsMap(v, depth + 1, seen);
    if (hit) return hit;
  }
  return null;
};

/**
 * Walk nested JSON (Liferay/BFF) until we find the map shaped like
 * `{ option_1: { additionalOutputFields: ... }, ... }`.
 */
const walkFindNishchitOptionResultsMap = (
  node,
  depth = 0,
  seen = new WeakSet(),
) => {
  if (depth > 14 || node == null) return null;

  if (typeof node === "string") {
    const trimmed = node.trim();
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
    return walkFindNishchitOptionResultsMap(
      normalizeNishchitBulkParsedRoot(node),
      depth + 1,
      seen,
    );
  }

  if (typeof node !== "object") return null;
  if (seen.has(node)) return null;
  seen.add(node);

  if (Array.isArray(node)) {
    return walkNishchitArrayChildren(node, depth, seen);
  }

  if (isOptionResultsMap(node)) return node;
  return walkNishchitObjectChildren(node, depth, seen);
};

const extractResultsFromNestedData = (parsed) => {
  const nested = parsed.data;
  if (nested == null) return null;

  const inner =
    typeof nested === "string"
      ? normalizeNishchitBulkParsedRoot(nested)
      : nested;
  if (!isPlainObject(inner)) return null;

  const fromResults = unwrapNishchitResultsValue(inner.results);
  if (fromResults) return fromResults;

  return walkFindNishchitOptionResultsMap(inner, 0, new WeakSet());
};

/**
 * Liferay / API gateways sometimes wrap the workbook JSON (e.g. under `data` or as a string).
 * Without this, `parsed.results` is missing and the UI gets `{}` → all "—" / Pay ₹0.
 */
const extractNishchitBulkResultsMap = (parsed) => {
  if (parsed == null) return null;
  if (typeof parsed === "string") {
    return extractNishchitBulkResultsMap(
      normalizeNishchitBulkParsedRoot(parsed),
    );
  }
  if (!isPlainObject(parsed)) {
    return walkFindNishchitOptionResultsMap(parsed, 0, new WeakSet());
  }

  const fromResults = unwrapNishchitResultsValue(parsed.results);
  if (fromResults) return fromResults;

  const fromNestedData = extractResultsFromNestedData(parsed);
  if (fromNestedData) return fromNestedData;

  return walkFindNishchitOptionResultsMap(parsed, 0, new WeakSet());
};

const pickResultsRowForPlanOption = (results, planOptionId) => {
  const key = String(planOptionId);
  let row = results[key];
  if (row && typeof row === "object" && !Array.isArray(row)) return row;
  const lower = key.toLowerCase();
  const found = Object.keys(results).find(
    (k) => String(k).toLowerCase() === lower,
  );
  if (!found) return null;
  row = results[found];
  return row && typeof row === "object" && !Array.isArray(row) ? row : null;
};

const mapNishchitBulkResultsToPerOption = (
  leadResult,
  planOptionIds,
  results,
) => {
  const out = {};
  planOptionIds.forEach((id) => {
    const key = String(id);
    let row = pickResultsRowForPlanOption(results, key);
    if (typeof row === "string") {
      row = normalizeNishchitBulkParsedRoot(row);
    }
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      out[key] = null;
      return;
    }
    const addl = normalizeAdditionalOutputFields(row.additionalOutputFields);
    const annuityPerFrequency =
      coerceAnnuityScalar(addl.annuityPerFrequency) ??
      coerceAnnuityScalar(addl.annuity_per_frequency) ??
      coerceAnnuityScalar(row.annuityPerFrequency);
    const annuityRate =
      coerceAnnuityScalar(addl.annuityRate) ??
      coerceAnnuityScalar(addl.annuity_rate) ??
      coerceAnnuityScalar(row.annuityRate);
    out[key] = {
      productName: leadResult?.assignedProductName,
      calculationSuccessful: row.calculationSuccessful,
      errorCode: row.errorCode,
      errorMessage: row.errorMessage,
      annuityPerFrequency,
      annuityRate,
      additionalOutputFields: addl,
    };
  });
  return out;
};

const resolveNishchitBulkApplicationNumber = (
  overrides,
  fullBase,
  leadResult,
) =>
  toStringOrEmpty(
    overrides?.applicationNumber ??
      fullBase?.applicationNumber ??
      leadResult?.applicationNumber ??
      leadResult?.application_number ??
      leadResult?.leadInput?.applicationNumber ??
      leadResult?.leadInput?.application_number ??
      getApplicationNumberFromUrl(),
  );

const buildNishchitBulkBaseRequest = (leadResult, overrides, planOptionIds) => {
  const anchorPlanOption =
    planOptionIds.find((id) => isJointPlanOption(id)) ||
    planOptionIds[0] ||
    "option_1";
  const mergedOverrides = {
    ...overrides,
    additionalInputFields: {
      ...(overrides.additionalInputFields || {}),
      planOption: anchorPlanOption,
    },
  };
  const fullBase = buildPremiumRequestFromLead(leadResult, mergedOverrides);
  if (!fullBase?.productCode) {
    throw new Error(
      "Product code is missing for Nishchit bulk premium calculation.",
    );
  }

  const additional = { ...(fullBase.additionalInputFields || {}) };
  delete additional.planOption;

  return {
    ...fullBase,
    applicationNumber: resolveNishchitBulkApplicationNumber(
      overrides,
      fullBase,
      leadResult,
    ),
    additionalInputFields: additional,
  };
};

const parseNishchitBulkPremiumResponse = (data, leadResult, planOptionIds) => {
  const parsed = normalizeNishchitBulkParsedRoot(data);
  const results = extractNishchitBulkResultsMap(parsed);
  if (!results || typeof results !== "object") {
    return {};
  }
  return mapNishchitBulkResultsToPerOption(leadResult, planOptionIds, results);
};

const executeNishchitBulkPremiumRequest = (
  leadResult,
  baseRequest,
  planOptionIds,
) => {
  const planOptions = planOptionIds.map((id) => String(id));
  const payload = {
    baseRequest: encryptPremiumRequestFields(baseRequest),
    planOptions,
  };

  if (import.meta.env?.VITE_DEBUG_NISCHIT_CALCULATE === "true") {
    logDebug("[Nishchit bulk calculate payload]", payload);
  }

  const cacheKey = stableSerialize({ baseRequest, planOptions });
  const existing = nishchitBulkPremiumInFlightCache.get(cacheKey);
  if (existing) {
    return existing.then((r) => cloneSerializable(r));
  }

  const resultPromise = api
    .post(API_ENDPOINTS.CALCULATE_NISHCHIT_BULK, payload)
    .then((response) =>
      parseNishchitBulkPremiumResponse(
        response?.data ?? null,
        leadResult,
        planOptionIds,
      ),
    )
    .catch((err) => {
      logError("[calculateNishchitBulkPremiumFromLead] Request failed", err);
      throw err;
    });

  nishchitBulkPremiumInFlightCache.set(cacheKey, resultPromise);
  resultPromise.finally(() => {
    if (nishchitBulkPremiumInFlightCache.get(cacheKey) === resultPromise) {
      nishchitBulkPremiumInFlightCache.delete(cacheKey);
    }
  });

  return resultPromise.then((r) => cloneSerializable(r));
};

/**
 * Nishchit Pension: one API call for multiple `planOptions` (same base request as single calculate).
 * @param {object} leadResult
 * @param {object} overrides same shape as {@link calculatePremiumFromLead}
 * @param {string[]} planOptionIds e.g. ["option_1","option_2"]
 * @returns {Record<string, object|null>} per-option result with annuityPerFrequency / annuityRate
 */
export const calculateNishchitBulkPremiumFromLead = async (
  leadResult,
  overrides = {},
  planOptionIds = [],
) => {
  if (context?.journeyType !== "nischit-pension") {
    throw new Error(
      "Bulk Nishchit premium is only available for the nischit-pension journey.",
    );
  }
  if (!Array.isArray(planOptionIds) || planOptionIds.length === 0) {
    return {};
  }

  const baseRequest = buildNishchitBulkBaseRequest(
    leadResult,
    overrides,
    planOptionIds,
  );
  return executeNishchitBulkPremiumRequest(
    leadResult,
    baseRequest,
    planOptionIds,
  );
};

/**
 * Calculates premium for all dimensions (payment modes + payment term types) in a single API call.
 * This enables instant switching between payment modes and LP/RP options without additional API calls.
 *
 * @param {Object} leadResult - The lead/quote init result containing lead input and defaults
 * @param {Object} overrides - Optional overrides for the base request
 * @param {Object} options - Optional configuration options
 * @param {string} options.primaryMode - The primary/selected payment mode (default: from leadResult or 'Yearly')
 * @param {string} options.primaryTermType - 'LP' or 'RP' (default: 'RP')
 * @param {number} options.primaryPremiumPayingTerm - The selected premium paying term
 * @param {number[]} options.limitedPayOptions - LP term options to calculate (default: [5, 7, 10, 15])
 * @param {number} options.regularPayTerm - RP term value (default: policyTerm)
 * @param {boolean} options.calculateAllPaymentTermTypes - Whether to calculate both LP and RP (default: true)
 * @returns {Object} Multi-dimension result with primaryResult, allResults, limitedPayResults, regularPayResult
 */
const logMultiDimensionPremiumCall = (
  baseRequest,
  primaryMode,
  primaryTermType,
  requestPayload,
) => {
  if (!DEBUG_PREMIUM_MULTIPLEX) return;
  logDebug("[calculateMultiDimensionPremium] Calling API with:", {
    productCode: baseRequest.productCode,
    primaryMode,
    primaryTermType,
    limitedPayOptions: requestPayload.limitedPayTermOptions,
    regularPayTerm: requestPayload.regularPayTerm,
    calculateAllPaymentTermTypes: requestPayload.calculateAllPaymentTermTypes,
  });
};

const getMultiDimensionPremiumFromCache = (cacheKey, productCode, label) => {
  const cached =
    label === "cache"
      ? getCachedMultiDimensionPremiumResult(cacheKey)
      : multiDimensionPremiumInFlightCache.get(cacheKey);
  if (!cached) return null;
  if (DEBUG_PREMIUM_MULTIPLEX) {
    logDebug(`[calculateMultiDimensionPremium] ${label} hit`, { productCode });
  }
  return label === "cache"
    ? cached
    : cached.then((result) => cloneSerializable(result));
};

export const calculateMultiDimensionPremium = async (
  leadResult,
  overrides = {},
  options = {},
) => {
  try {
    const { primaryMode, primaryTermType, requestPayload } =
      buildMultiDimensionPremiumRequestPayload(leadResult, overrides, options);
    const baseRequest = requestPayload.baseRequest;
    const cacheKey = stableSerialize(requestPayload);

    logMultiDimensionPremiumCall(
      baseRequest,
      primaryMode,
      primaryTermType,
      requestPayload,
    );

    const cachedResult = getMultiDimensionPremiumFromCache(
      cacheKey,
      baseRequest.productCode,
      "cache",
    );
    if (cachedResult) return cachedResult;

    const inFlightResult = getMultiDimensionPremiumFromCache(
      cacheKey,
      baseRequest.productCode,
      "in-flight dedupe",
    );
    if (inFlightResult) return inFlightResult;

    const resultPromise = api
      .post(API_ENDPOINTS.CALCULATE_MULTI_PREMIUM, requestPayload)
      .then((response) =>
        processMultiDimensionPremiumResponse(
          response?.data ?? null,
          leadResult,
          cacheKey,
        ),
      )
      .catch((error) => {
        const message = resolveMultiDimensionPremiumErrorMessage(error);
        logError(
          "[calculateMultiDimensionPremium] Failed to calculate multi-dimension premium",
          { message },
        );
        throw new Error(message);
      })
      .finally(() => {
        multiDimensionPremiumInFlightCache.delete(cacheKey);
      });

    multiDimensionPremiumInFlightCache.set(cacheKey, resultPromise);
    return resultPromise.then((result) => cloneSerializable(result));
  } catch (error) {
    logError(
      "[calculateMultiDimensionPremium] Failed to prepare multi-dimension premium request",
      error,
    );
    throw error;
  }
};

/**
 * Calculates premium for all (coverAmount, policyTerm) variations in a single API call.
 * Each variation returns a multi-dimension result for the requested payment mode(s) (default: primaryMode only)
 * plus LP/RP combinations for that mode. Pass options.premiumPaymentModes to override (e.g. all modes).
 * The frontend caches the response for the active frequency.
 *
 * @param {Object} leadResult - The lead/quote init result
 * @param {Object} overrides - Overrides for the base request (riders, ROP/ECBO, etc.)
 * @param {Array<{baseSumAssured: number, policyTerm: number}>} variations - All (SA, PT) combos to calculate
 * @param {Object} options - primaryMode, primaryTermType, limitedPayOptions, etc.
 * @returns {Object} { results: { "SA_PT": multiDimensionResult }, failedVariations, calculationSuccessful }
 */
const validateBulkPremiumInputs = (baseRequest, variations) => {
  if (!baseRequest?.productCode) {
    throw new Error("Product code is missing for bulk premium calculation.");
  }
  if (!variations.length) {
    throw new Error(
      "At least one variation is required for bulk premium calculation.",
    );
  }
  if (
    !baseRequest.applicationNumber ||
    String(baseRequest.applicationNumber).trim() === ""
  ) {
    throw new Error(
      "Application number is required for bulk premium calculation.",
    );
  }
};

const buildBulkPremiumRequestPayload = (baseRequest, variations, options) => {
  const {
    primaryMode = options.leadResult?.premiumDefaults?.premiumPaymentMode ||
      "Yearly",
    primaryTermType = PAYMENT_TERM_TYPE.REGULAR_PAY,
    primaryPremiumPayingTerm = null,
    limitedPayOptions = DEFAULT_LIMITED_PAY_OPTIONS,
  } = options;

  const paymentModesForRequest =
    Array.isArray(options.premiumPaymentModes) &&
    options.premiumPaymentModes.length
      ? options.premiumPaymentModes
      : [primaryMode];

  return {
    baseRequest: encryptPremiumRequestFields(baseRequest),
    primaryPremiumPaymentMode: primaryMode,
    primaryPaymentTermType: primaryTermType,
    primaryPremiumPayingTerm:
      primaryPremiumPayingTerm ||
      (primaryTermType === PAYMENT_TERM_TYPE.LIMITED_PAY
        ? limitedPayOptions[0]
        : variations[0]?.policyTerm || baseRequest.policyTerm),
    premiumPaymentModes: paymentModesForRequest,
    limitedPayTermOptions: limitedPayOptions,
    returnAllResults: true,
    variations: variations.map((v) => ({
      baseSumAssured: v.baseSumAssured,
      policyTerm: v.policyTerm,
    })),
  };
};

const processBulkPremiumResponse = (data, leadResult) => {
  const parsed = parsePremiumResponseData(data);
  if (!parsed || typeof parsed !== "object") {
    throw new Error(
      "Bulk premium service returned an empty or invalid response body.",
    );
  }

  if (
    parsed.calculationSuccessful === false &&
    (!parsed.results || Object.keys(parsed.results).length === 0)
  ) {
    throw new Error(
      parsed.errorMessage ||
        "Bulk premium calculation failed for all variations.",
    );
  }

  const productName = leadResult?.assignedProductName;
  if (parsed.results) {
    Object.values(parsed.results).forEach((multiResult) => {
      attachProductNameToBulkMultiResult(multiResult, productName);
    });
  }

  if (DEBUG_PREMIUM_MULTIPLEX) {
    logDebug("[calculateBulkPremium] API response:", {
      calculationSuccessful: parsed.calculationSuccessful,
      calculationTimeMs: parsed.calculationTimeMs,
      resultCount: parsed.results ? Object.keys(parsed.results).length : 0,
      failedCount: parsed.failedVariations
        ? Object.keys(parsed.failedVariations).length
        : 0,
    });
  }

  return parsed;
};

const resolveBulkPremiumErrorMessage = (error) =>
  error?.response?.data?.errorMessage ||
  error?.response?.data?.message ||
  error?.message ||
  "Bulk premium calculation could not be completed.";

export const calculateBulkPremium = async (
  leadResult,
  overrides = {},
  variations = [],
  options = {},
) => {
  try {
    const baseRequest = buildPremiumRequestFromLead(leadResult, overrides);
    validateBulkPremiumInputs(baseRequest, variations);

    const {
      primaryMode = leadResult?.premiumDefaults?.premiumPaymentMode || "Yearly",
      primaryTermType = PAYMENT_TERM_TYPE.REGULAR_PAY,
    } = options;

    const requestPayload = buildBulkPremiumRequestPayload(
      baseRequest,
      variations,
      {
        ...options,
        leadResult,
      },
    );

    if (DEBUG_PREMIUM_MULTIPLEX) {
      logDebug("[calculateBulkPremium] Calling API with:", {
        productCode: baseRequest.productCode,
        primaryMode,
        primaryTermType,
        variationCount: variations.length,
      });
    }

    const { signal } = options;
    const response = await api.post(
      API_ENDPOINTS.CALCULATE_BULK_PREMIUM,
      requestPayload,
      signal ? { signal } : undefined,
    );

    return processBulkPremiumResponse(response?.data ?? null, leadResult);
  } catch (error) {
    const message = resolveBulkPremiumErrorMessage(error);
    logError("[calculateBulkPremium] Failed", { message });
    throw new Error(message);
  }
};

/**
 * Gets the cached premium result for a specific payment mode from multi-dimension results.
 * Falls back to primaryResult if the mode is not found.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {string} premiumPaymentMode - The payment mode to look up
 * @returns {Object} The premium calculation result for the specified mode
 */
export const getPremiumResultForMode = (
  multiDimensionResult,
  premiumPaymentMode,
) => {
  if (!multiDimensionResult) return null;

  // Try to get from allResults map first
  if (
    multiDimensionResult.allResults &&
    multiDimensionResult.allResults[premiumPaymentMode]
  ) {
    return multiDimensionResult.allResults[premiumPaymentMode];
  }

  // Fallback to primaryResult if mode matches or not found
  if (
    multiDimensionResult.primaryPremiumPaymentMode === premiumPaymentMode ||
    !multiDimensionResult.allResults
  ) {
    return multiDimensionResult.primaryResult;
  }

  return null;
};

/**
 * Gets the cached Limited Pay (LP) premium result for a specific term.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {number} premiumPayingTerm - The LP term to look up (5, 7, 10, 15)
 * @returns {Object} The premium calculation result for the specified LP term
 */
export const getLimitedPayResult = (
  multiDimensionResult,
  premiumPayingTerm,
) => {
  if (!multiDimensionResult) return null;

  // Try to get from limitedPayResults map
  if (multiDimensionResult.limitedPayResults) {
    // Handle both string and number keys (JSON may deserialize as strings)
    const term = String(premiumPayingTerm);
    if (multiDimensionResult.limitedPayResults[term]) {
      return multiDimensionResult.limitedPayResults[term];
    }
    if (multiDimensionResult.limitedPayResults[premiumPayingTerm]) {
      return multiDimensionResult.limitedPayResults[premiumPayingTerm];
    }
  }

  // Fallback to primaryResult if term type is LP and matches
  if (
    multiDimensionResult.primaryPaymentTermType ===
      PAYMENT_TERM_TYPE.LIMITED_PAY &&
    multiDimensionResult.primaryPremiumPayingTerm === premiumPayingTerm
  ) {
    return multiDimensionResult.primaryResult;
  }

  return null;
};

/**
 * Gets the cached Regular Pay (RP) premium result.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @returns {Object} The premium calculation result for Regular Pay
 */
export const getRegularPayResult = (multiDimensionResult) => {
  if (!multiDimensionResult) return null;

  // Return regularPayResult if available
  if (multiDimensionResult.regularPayResult) {
    return multiDimensionResult.regularPayResult;
  }

  // Fallback to primaryResult if term type is RP
  if (
    multiDimensionResult.primaryPaymentTermType ===
    PAYMENT_TERM_TYPE.REGULAR_PAY
  ) {
    return multiDimensionResult.primaryResult;
  }

  return null;
};

/**
 * Gets all Limited Pay results as an array sorted by term.
 * Useful for displaying all LP options with their premiums.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @returns {Array<{term: number, result: Object}>} Array of LP results sorted by term
 */
export const getAllLimitedPayResults = (multiDimensionResult) => {
  if (!multiDimensionResult?.limitedPayResults) return [];

  return Object.keys(multiDimensionResult.limitedPayResults)
    .map((term) => ({
      term: Number(term),
      result: multiDimensionResult.limitedPayResults[term],
    }))
    .sort((a, b) => a.term - b.term);
};

/**
 * Gets a combined result for a specific combination of dimensions.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {string} premiumPaymentMode - The payment mode
 * @param {string} paymentTermType - 'LP' or 'RP'
 * @param {number} premiumPayingTerm - The premium paying term
 * @returns {Object} The premium calculation result for the combination
 */
export const getCombinedResult = (
  multiDimensionResult,
  premiumPaymentMode,
  paymentTermType,
  premiumPayingTerm,
) => {
  if (!multiDimensionResult?.combinedResults) return null;

  const key = `${premiumPaymentMode}_${paymentTermType}_${premiumPayingTerm}`;
  return multiDimensionResult.combinedResults[key] || null;
};

/**
 * Gets the premium result based on payment term type selection.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {string} paymentTermType - 'LP' or 'RP'
 * @param {number} premiumPayingTerm - Required for LP, optional for RP
 * @returns {Object} The premium calculation result
 */
export const getPremiumResultForTermType = (
  multiDimensionResult,
  paymentTermType,
  premiumPayingTerm,
) => {
  if (!multiDimensionResult) return null;

  if (paymentTermType === PAYMENT_TERM_TYPE.LIMITED_PAY) {
    return getLimitedPayResult(multiDimensionResult, premiumPayingTerm);
  }

  return getRegularPayResult(multiDimensionResult);
};

/**
 * Gets the premium result for the full combination: frequency + payment term type + LP term.
 * Same dimension concept as backend: combinedResults key = "{mode}_{LP|RP}_{term}".
 * Prefer combinedResults so each option (e.g. LP 5/7/10/15) shows its own premium.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {string} premiumPaymentMode - e.g. 'Yearly', 'Monthly', 'Quarterly', 'Half-yearly'
 * @param {string} paymentTermType - 'LP' or 'RP', or UI string 'Limited Pay' / 'Regular Pay'
 * @param {number|null} premiumPayingTerm - LP term (5, 7, 10, 15) or 0/null for RP
 * @returns {Object|null} The premium calculation result for that combination
 */
export const getPremiumResultForDimensions = (
  multiDimensionResult,
  premiumPaymentMode,
  paymentTermType,
  premiumPayingTerm,
) => {
  if (!multiDimensionResult) return null;

  const isLP =
    paymentTermType === PAYMENT_TERM_TYPE.LIMITED_PAY ||
    paymentTermType === "Limited Pay";
  const termType = isLP
    ? PAYMENT_TERM_TYPE.LIMITED_PAY
    : PAYMENT_TERM_TYPE.REGULAR_PAY;
  const pptForKey = isLP ? Number(premiumPayingTerm) || 0 : 0;

  const combined = getCombinedResult(
    multiDimensionResult,
    premiumPaymentMode,
    termType,
    pptForKey,
  );
  if (combined) return combined;

  const termResult = getPremiumResultForTermType(
    multiDimensionResult,
    termType,
    termType === PAYMENT_TERM_TYPE.LIMITED_PAY ? premiumPayingTerm : undefined,
  );
  if (termResult?.allResults?.[premiumPaymentMode])
    return termResult.allResults[premiumPaymentMode];
  if (
    termResult &&
    multiDimensionResult.primaryPremiumPaymentMode === premiumPaymentMode
  )
    return termResult;

  return getPremiumResultForMode(multiDimensionResult, premiumPaymentMode);
};
